import type { RJSFSchema } from '@rjsf/utils'
import Form, { type IChangeEvent } from '@rjsf/core'
import { useNavigate } from 'react-router'
import { useCreatePayloadSchemaMutation } from '../../hooks/usePayloadSchema'
import { templates, widgets } from '../ui-schema-components'
import { ModalDialog, Mtds, useErrorToast } from 'prove-common'
import { customizeValidator } from '@rjsf/validator-ajv8'

export type PayloadSchemaFormData = {
  name: string
}

export const payloadSchema: RJSFSchema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      title: 'Navn'
    }
  },
  required: ['name']
}

type CreateSchemaModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  contractId: number | undefined
  labId: number | undefined
}

export const CreateSchemaModal = ({
  isOpen,
  setOpen,
  contractId,
  labId
}: CreateSchemaModalProps) => {
  const navigate = useNavigate()
  const createPayloadSchema = useCreatePayloadSchemaMutation()
  const showError = useErrorToast('opprette registreringsskjemaet')
  const onSubmit = async ({
    formData
  }: IChangeEvent<PayloadSchemaFormData>): Promise<void> => {
    if (formData != null) {
      const defaultSchema =
        '{\n' +
        '    "type": "object",\n' +
        '    "properties": {},\n' +
        '    "required": []\n' +
        '}'
      try {
        const value = await createPayloadSchema.mutateAsync({
          name: formData.name,
          schema: defaultSchema,
          labId: labId
        })
        Mtds.toast.success('Nytt registreringsskjema er opprettet')
        const schemaId = value.id
        const url = contractId
          ? `/contracts/${contractId}/schemas/${schemaId}`
          : `/schemas/${schemaId}`
        navigate(url)
      } catch (error) {
        showError(error)
      }
    }
  }
  const validator = customizeValidator<PayloadSchemaFormData>()
  return (
    <ModalDialog
      id='create-schema-modal'
      closedby='any'
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <Mtds.Button
        command='close'
        commandfor='create-schema-modal'
        aria-label='Lukk'
      />

      <Mtds.Prose>
        <Mtds.Heading>Opprett registreringsskjema</Mtds.Heading>
        <Form<PayloadSchemaFormData>
          noHtml5Validate
          schema={payloadSchema}
          widgets={widgets}
          templates={templates}
          showErrorList={false}
          validator={validator}
          onSubmit={onSubmit}
          experimental_defaultFormStateBehavior={{
            constAsDefaults: 'skipOneOf'
          }}
        >
          <Mtds.Flex className='mt-8'>
            <Mtds.Button command='close' commandfor='create-schema-modal'>
              Lukk
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='submit'
              aria-busy={createPayloadSchema.isPending}
              disabled={createPayloadSchema.isPending}
            >
              Opprett
            </Mtds.Button>
          </Mtds.Flex>
        </Form>
      </Mtds.Prose>
    </ModalDialog>
  )
}
